import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Videocamera from '@/images/Videocamera.svg';
import theme from '@/theme';
import styled from '@emotion/styled';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useState } from 'react';

const StyledDropdown = styled(Button)<{ selected?: boolean; lightTheme?: boolean }>`
    display: flex;
    align-items: center;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    letter-spacing: 0;
    top: 20px;
    border-radius: 8px;
    position: unset;
    border-color: ${({ theme, lightTheme }) => (lightTheme ? theme.colors.black[700] : theme.colors.black[300])} !important;
    background-color: ${({ theme, lightTheme }) => (lightTheme ? theme.colors.secondary.main : '#fff')} !important;
    gap: 8px;

    p {
        white-space: nowrap;
        max-width: 96px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const StyledMenu = styled(Menu)`
    & .MuiPaper-root {
        padding-left: 8px;
        padding-right: 8px;
        border-radius: 12px;
        box-shadow: 0px 10px 60px 0px rgba(0, 0, 0, 0.1);
        margin-top: 8px;
    }
`;

const StyledMenuItem = styled(MenuItem, { shouldForwardProp: p => p !== 'selected' })<{ selected: boolean }>`
    background-color: ${({ selected, theme }) => (selected ? theme.colors.black[100] : '#ffffff')} !important;
    display: flex;
    height: 40px;
    padding: 12px 16px;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: 8px;
    color: ${({ theme }) => theme.colors.secondary.main};
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 18px;
    letter-spacing: -0.28px;
`;

interface Props {
    icon: string;
    options: MediaDeviceInfo[];
    value: string;
    onChange: (device: string) => void;
    showLabel?: boolean;
    iconColor?: string;
    lightTheme?: boolean;
}

const BLACK = '#000000';

export const DeviceSelect = ({
    options,
    value,
    onChange,
    showLabel = true,
    icon = Videocamera,
    iconColor = BLACK,
    lightTheme = false,
}: Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleSelect = (device: string) => () => {
        onChange(device);
    };

    const selected = options.find(o => o.deviceId === value);

    return (
        <>
            <StyledDropdown
                id="droplist-button"
                aria-controls={open ? 'droplist-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                variant="outlined"
                color="secondary"
                disabled={options.length === 0}
                disableRipple
                selected={open}
                lightTheme={lightTheme}
                endIcon={
                    open ? (
                        <ExpandLess sx={{ color: lightTheme ? theme.colors.white : theme.colors.secondary.main }} />
                    ) : (
                        <ExpandMore sx={{ color: lightTheme ? theme.colors.white : theme.colors.secondary.main }} />
                    )
                }>
                <MaskedIcon src={icon} size="24px" color={iconColor} />
                {showLabel && <p>{selected?.label || (options.length ? 'Select a device' : 'No devices available')}</p>}
            </StyledDropdown>
            <StyledMenu id="droplist-menu" anchorEl={anchorEl} open={open} onClose={handleClose}>
                {options.map(o => (
                    <StyledMenuItem key={o.deviceId} onClick={handleSelect(o.deviceId)} selected={o.deviceId === value}>
                        {o.label || `Device ${o.deviceId}`}
                    </StyledMenuItem>
                ))}
            </StyledMenu>
        </>
    );
};
